import React, {useState} from 'react';
import classnames from 'classnames';

import {Button, Dialogue, TextField, Modal, addToast, Anchor} from '@albert-io/atomic';
import {resource} from '@albert-io/json-api-framework/request/builder';

import {TemplateModelV1} from 'resources/GeneratedModels/Template/TemplateModel.v1';
import {invalidatePartialInterest} from 'resources/mandark.resource';
import {invalidateFilters} from 'components/PracticeView/PracticeViewToolbar/Filters/filters.utils';
import masqueradeStore from 'client/generic/Masquerade/Masquerade.store';
import {getFolderDetailPath} from 'client/Folders/foldersRouting';

interface Props {
  handleClose: () => void;
  onSaveSuccess?: (newFolderId: string) => void;
}

export default function NewFolderModal({handleClose, onSaveSuccess = () => {}}: Props) {
  'use memo';

  const [newFolderName, setNewFolderName] = useState<string | null>(null);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const folderNameInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewFolderName(e.target.value);
  };

  const resetErrorMessage = () => {
    setHasError(false);
    setErrorMessage('');
  };

  /**
   * Creates a new folder and invalidates the resource to force auto update
   */
  const handleSave = async () => {
    const folderName = newFolderName;
    if (!folderName?.trim()) {
      setHasError(true);
      setErrorMessage("Folder name can't be blank.");
      return;
    }

    if (folderName.length > 256) {
      setHasError(true);
      setErrorMessage('Folder name must be 256 characters or less.');
      return;
    }

    try {
      const savedFolder = await new TemplateModelV1()
        .setName(folderName)
        .addRelationship({
          type: 'teacher_v1',
          relation: masqueradeStore.getUserIdByMasqueradeState()
        })
        .save();

      invalidatePartialInterest(resource('templates_v1').mandarkEndpoint(['templates_v1']).done());
      invalidateFilters();
      onSaveSuccess(savedFolder.getId());
      // Outside the Folders view there's no other success cue for a created folder.
      const folderLink = (
        <Anchor
          underlined
          inheritColor
          target='_blank'
          rel='noopener noreferrer'
          href={getFolderDetailPath(savedFolder.getId())}
        >
          {folderName}
        </Anchor>
      );
      addToast({
        color: 'positive',
        title: 'Success!',
        message: <span>{folderLink} was added to your folders list.</span>
      });
    } catch {
      addToast({
        color: 'negative',
        title: 'Error:',
        message: 'Something went wrong and your folder was not created'
      });
    }
    handleClose();
  };

  return (
    <Modal ariaLabel='Create New Folder Modal' handleClose={handleClose}>
      {({CloseButtonWrapper, modalContentStyle}) => {
        return (
          <Dialogue
            inModal
            hideCloseBtn
            className={classnames(modalContentStyle, 'folders-modal__new-folder-modal')}
          >
            <Dialogue.Body>
              <TextField
                border
                label='Enter a new folder name:'
                error={hasError}
                message={errorMessage}
                onChange={folderNameInputHandler}
                value={newFolderName}
                placeholder='Type folder name here...'
                onFocus={resetErrorMessage}
                data-testid='new-folder-name-input'
              />
            </Dialogue.Body>
            <Dialogue.BtnGroup>
              <CloseButtonWrapper>
                <Button variant='text'>Cancel</Button>
              </CloseButtonWrapper>
              <Button onClick={handleSave} data-testid='assignments_create-btn'>
                Create
              </Button>
            </Dialogue.BtnGroup>
          </Dialogue>
        );
      }}
    </Modal>
  );
}
